import type { ApiOptions, ApiResponse, ItemList } from '@/types/api'

const SESSION_TOKEN_KEY = 'zhidianbao_admin_token'
const REQUEST_TIMEOUT_MS = 15_000
const API_BASE = new URL('api/v1/admin/', document.baseURI).pathname.replace(/\/$/, '')
const MINIAPP_API_BASE = new URL('api/v1/miniapp/', document.baseURI).pathname.replace(/\/$/, '')

let unauthorizedHandler: (() => void) | undefined

export class ApiError extends Error {
  constructor(
    message: string,
    public status = 0,
    public code = 'REQUEST_FAILED',
    public details?: unknown
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

export const tokenStorage = {
  get: () => sessionStorage.getItem(SESSION_TOKEN_KEY),
  set: (token: string) => sessionStorage.setItem(SESSION_TOKEN_KEY, token),
  clear: () => sessionStorage.removeItem(SESSION_TOKEN_KEY)
}

export function onUnauthorized(handler: () => void) {
  unauthorizedHandler = handler
}

function requestKey() {
  if (crypto.randomUUID) return crypto.randomUUID()
  return `${Date.now()}-${crypto.getRandomValues(new Uint32Array(2)).join('-')}`
}

export async function apiRequest<T>(path: string, options: ApiOptions = {}): Promise<ApiResponse<T>> {
  const { method = 'GET', body, query, authenticated = true, idempotent = false } = options
  const base = path.startsWith('/owner/') ? MINIAPP_API_BASE : API_BASE
  const url = new URL(`${base}${path}`, window.location.origin)

  Object.entries(query ?? {}).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '' && value !== 'all') {
      url.searchParams.set(key, String(value))
    }
  })

  const headers: Record<string, string> = { Accept: 'application/json' }
  const token = tokenStorage.get()
  if (authenticated && token) headers.Authorization = `Bearer ${token}`
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  if (idempotent) headers['Idempotency-Key'] = requestKey()

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS)

  try {
    const response = await fetch(url, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal
    })
    const result = await response.json().catch(() => null) as ApiResponse<T> | null
    if (!response.ok) {
      if (response.status === 401 && authenticated) {
        tokenStorage.clear()
        unauthorizedHandler?.()
      }
      throw new ApiError(result?.message ?? `请求失败（${response.status}）`, response.status, result?.code, result)
    }
    if (!result) throw new ApiError('服务器返回了无效数据')
    return result
  } catch (error) {
    if (error instanceof ApiError) throw error
    if (error instanceof DOMException && error.name === 'AbortError') {
      throw new ApiError('服务器响应超时，请稍后重试', 0, 'REQUEST_TIMEOUT')
    }
    throw new ApiError('无法连接服务器，请检查网络后重试')
  } finally {
    window.clearTimeout(timeout)
  }
}

export async function apiRequestAll<T>(path: string, options: ApiOptions = {}) {
  const items: T[] = []
  let page = 1
  let hasMore = false
  do {
    const result = await apiRequest<ItemList<T>>(path, {
      ...options,
      query: { ...options.query, page, pageSize: 100 }
    })
    items.push(...(result.data.items ?? []))
    hasMore = Boolean(result.data.hasMore)
    page += 1
  } while (hasMore)
  return items
}
